import axios from 'axios'
import { useContext, useEffect, useState } from 'react'

import { ShotlistsService } from '@/services/shotlist.service'

import keycloak from '@/auth/keycloak'
import { STATUS, STEP_TYPE } from '@/constants/common'
import { useTaskWatch } from '@/hooks/useTaskWatch'
import { toastError } from '@/utils/toastError'

import {
	DraftState,
	DraftsContext
} from '../components/shotlistUpload/DraftsContext'

import { useTimer } from './useTimer'

interface IUseUploadFileProcessParams {
	file?: File
	draftState: DraftState
	selectedWay: string
	setIsUploading: (value: boolean) => void
	setIsTranscribing: (value: boolean) => void
}

export const useUploadFileProcess = ({
	file,
	draftState,
	selectedWay,
	setIsUploading,
	setIsTranscribing
}: IUseUploadFileProcessParams) => {
	const { draft, controller } = draftState
	const taskId = draft.id

	const [progress, setProgress] = useState(0)
	const [error, setError] = useState(false)
	const [localHasVideo, setLocalHasVideo] = useState(draft.has_video)
	const [isUploaded, setIsUploaded] = useState(
		draft.status === STATUS.ADDED && draft.has_video && !draft.substatus
	)

	const { timer, startTimer, clearTimer } = useTimer()
	const { watchTask, clearWatch } = useTaskWatch()
	const { setController, updateDraftStatus } = useContext(DraftsContext)

	// Watching transcribing process
	const runWatchTask = () => {
		watchTask(
			taskId,
			shotlist => {
				updateDraftStatus(taskId, shotlist.status)
				setLocalHasVideo(shotlist.has_video)
			},
			shotlist => {
				clearTimer()
				clearWatch()
				setIsUploading(false)
				setIsTranscribing(false)
				updateDraftStatus(taskId, shotlist.status)
				setLocalHasVideo(shotlist.has_video)

				if (shotlist.status === STATUS.FAILED) {
					setError(true)
					toastError(
						'Transcription failed. Open the shotlist and try to start transcription again.',
						{ toastId: `transcription-failed-${taskId}` }
					)
				}
			}
		)
	}

	const handleStart = async (language: string) => {
		if (!file) {
			throw new Error('No file provided')
		}

		let stage: 'upload' | 'hls' | 'transcribe' = 'upload'
		let uploadController: AbortController | undefined

		try {
			setIsUploading(true)

			// Refresh token before uploading to avoid failing!
			await keycloak.updateToken(1200)

			// Abort controller for every query is saved in Context
			uploadController = new AbortController()
			setController(taskId, uploadController)

			// File uploading with progress
			stage = 'upload'
			const { job_id, task_id } = await ShotlistsService.uploadShotlistVideo(
				taskId,
				file,
				event => {
					const percent = Math.round((event.loaded * 100) / (event.total || 1))
					setProgress(percent)
				},
				uploadController.signal
			)

			if (!job_id) {
				return
			}

			// HLS generating after uploading video
			stage = 'hls'
			await ShotlistsService.pollVideoJobUntilReady(task_id, job_id)

			if (selectedWay === STEP_TYPE.UPLOAD) setIsUploading(false)

			if (!language.length) return
			await ShotlistsService.updateShotlistLanguage(taskId, language)

			// Transcribing with progress timer
			startTimer()
			setIsTranscribing(true)
			stage = 'transcribe'
			await ShotlistsService.transcribeShotlist(taskId)

			// Watching transcribing process
			runWatchTask()
		} catch (error) {
			if (axios.isCancel(error) || uploadController?.signal.aborted) return

			setError(true)
			setIsUploading(false)
			clearTimer()
			clearWatch()
			setIsTranscribing(false)

			console.log(`Upload flow failed at ${stage} stage:`, error)

			toastError('Upload failed. Please delete this upload and start again.', {
				toastId: `upload-failed-${taskId}`
			})
		}
	}

	useEffect(() => {
		const fetchShotlist = async () => {
			const shotlist = await ShotlistsService.getShotlist(taskId)

			if (
				shotlist &&
				(shotlist.status === STATUS.ADDED ||
					shotlist.status === STATUS.FINISHED) &&
				shotlist.has_video &&
				!shotlist.substatus
			) {
				setIsUploaded(true)
			} else {
				return
			}
		}

		fetchShotlist()
	}, [])

	// Resume showing the upload and transcription process after a user has left the page and returned
	useEffect(() => {
		const watchProgress = async () => {
			const draft = await ShotlistsService.getShotlist(taskId)

			// if this is the Upload flow - return
			if (
				draft &&
				draft.has_video &&
				draft.status === STATUS.ADDED &&
				!draft.substatus
			)
				return

			if (
				draft &&
				draft.status !== STATUS.FINISHED &&
				!file &&
				controller !== undefined
			) {
				setIsUploading(true)
				startTimer()
				setIsTranscribing(true)
				// Watching transcribing process
				runWatchTask()
			}
		}

		watchProgress()

		return () => {
			clearTimer()
			clearWatch()
		}
	}, [draft.id])

	return {
		progress,
		error,
		localHasVideo,
		isUploaded,
		timer,
		watchTask,
		clearWatch,
		clearTimer,
		handleStart,
		setLocalHasVideo
	}
}
